import React, { useState, useEffect, useMemo, useRef } from 'react';
import { formulaApi, getErrorMessage } from '../utils/api';
import {
  DEFAULT_LABOR_RATE,
  arrangementSubtotal,
  arrangementTotal,
  laborAmount,
  totalStemCount,
  formatCurrency,
  buildBreakdownText,
  effectiveRetail,
  vendorMargin,
} from '../utils/formulaPricing';
import VendorOfferPicker from './proposals/VendorOfferPicker';
import SubstitutePanel from './proposals/SubstitutePanel';
import { substituteEmphasis } from '../utils/substituteTriggers';
import PageHeader from './PageHeader';
import { FlowerIcon, XIcon, RowsIcon, SearchIcon, PlusIcon } from './icons/LucideIcons';

const TABS = [
  { id: 'vase', label: 'Vase' },
  { id: 'flowers', label: 'Flowers' },
  { id: 'labor', label: 'Labor' },
];

const circleBtn =
  'flex items-center justify-center shrink-0 rounded-full border border-gray-300 bg-white text-floral-700 font-semibold leading-none hover:bg-floral-50 active:bg-floral-100 transition-colors [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900';

const fieldBox =
  'flex items-center gap-2 px-3 py-4 bg-white border border-gray-300 rounded-md focus-within:border-floral-500 focus-within:ring-[3px] focus-within:ring-floral-100';

// Card head band for the rail cards (as .card-header, but these cards don't
// clip: the modal's sticky footer and sticky banner live inside them).
const RAIL_CARD = 'rounded-lg border border-gray-200 bg-white';
const RAIL_HEAD = 'flex items-center justify-between gap-3 rounded-t-lg border-b border-gray-200 bg-floral-50 px-[18px] py-3';

// Compact price for component tiles/lines: trims trailing zeros so
// $5.00 → "$5", $5.50 → "$5.5", $5.25 stays "$5.25".
function formatCompact(amount) {
  const n = Number(amount) || 0;
  const s = n.toFixed(2).replace(/\.?0+$/, '');
  return `$${s}`;
}

// Signed price delta, e.g. "+$10" or "−$5.5" — trailing zeros trimmed.
function formatDelta(n) {
  const abs = Math.abs(n);
  const s = abs.toFixed(2).replace(/\.?0+$/, '');
  return `${n > 0 ? '+' : '−'}$${s}`;
}

// Flower category badges shown on tiles.
// Semantic tones in place of the old floral / amber / purple / sky.
const CATEGORY_META = {
  focal: { label: 'Focal', cls: 'bg-floral-100 text-floral-900' },
  line: { label: 'Line', cls: 'bg-warn-bg text-warn-fg' },
  mass: { label: 'Mass', cls: 'bg-ready-bg text-ready-fg' },
  filler: { label: 'Filler', cls: 'bg-info-bg text-info-fg' },
};

// Fuzzy match: a direct substring hit, or the query's characters appearing
// in order anywhere in the name.
function fuzzyMatch(query, text) {
  const q = (query || '').toLowerCase().trim();
  if (!q) return true;
  const t = (text || '').toLowerCase();
  if (t.includes(q)) return true;
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

const Tile = ({ item, selected, count, delta, price, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative flex flex-col rounded-md border text-left transition-colors duration-[120ms] overflow-hidden ${
      selected
        ? 'border-floral-700 ring-[3px] ring-floral-100 bg-floral-50'
        : 'border-gray-200 hover:border-floral-500 bg-white'
    }`}
  >
    <div className="relative aspect-square w-full overflow-hidden bg-floral-50 flex items-center justify-center">
      {item.photoUrl ? (
        <img src={item.photoUrl} alt={item.name} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <FlowerIcon size={28} strokeWidth={1.75} className="text-floral-300" />
      )}
    </div>
    <div className="px-2 py-1.5">
      <span className="text-xs font-medium text-gray-900 truncate block">
        {item.name}
      </span>
    </div>
    {count > 0 && (
      <span className="absolute top-1 right-1 bg-floral-700 text-white text-xs font-semibold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center">
        {count}
      </span>
    )}
    {typeof delta === 'number' && delta !== 0 && (
      <span
        className={`absolute top-1 right-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
          delta > 0
            ? 'bg-ok-bg text-ok-fg'
            : 'bg-danger-bg text-danger-fg'
        }`}
      >
        {formatDelta(delta)}
      </span>
    )}
  </button>
);

export function toStemRow(f, i, flowerCounts) {
  return {
    stem_key: f.id,
    stem_label: f.name,
    category: f.category || null,
    stems_per_arrangement: flowerCounts[f.id] || 0,
    wholesale_unit_cost: Number(f.vendorCost) > 0 ? Number(f.vendorCost) : (Number(f.pricePerStem) || 0),
    vendor_offer_id: f.vendorOfferId || null,
    unit_count: f.unitCount || null,
    position: i,
  };
}

const FormulaBuilder = ({ onCommit, onClose, existingItem, initialFormula, eventDate }) => {
  const [priceList, setPriceList] = useState({
    vases: [],
    flowers: [],
    labor: { rates: [22.5, 20], defaultRate: 22.5 },
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [activeTab, setActiveTab] = useState('vase');
  const [search, setSearch] = useState('');
  const [showTypeTabs, setShowTypeTabs] = useState(false);
  const [flowerTypeFilter, setFlowerTypeFilter] = useState('all');
  // Session-only custom stems: lets staff add one-off seasonal stems
  // without saving them to the Settings price list.
  const [customFlowers, setCustomFlowers] = useState([]);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customDraft, setCustomDraft] = useState({ name: '', price: '' });
  const [selectedVaseId, setSelectedVaseId] = useState(null);
  const [flowerCounts, setFlowerCounts] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [pricingMode, setPricingMode] = useState('budget');
  const [budget, setBudget] = useState('');
  const [budgetMode, setBudgetMode] = useState('perArrangement');
  const [laborRate, setLaborRate] = useState(DEFAULT_LABOR_RATE);
  const [nameOverride, setNameOverride] = useState(null);
  // Budget field is dual-mode: shows the remaining-spend countdown by
  // default, flips to the editable budget value when the user clicks the
  // field or a ±stepper.
  const [editingBudget, setEditingBudget] = useState(false);
  const budgetInputRef = useRef(null);
  const [showStickyBanner, setShowStickyBanner] = useState(false);
  const budgetSectionRef = useRef(null);

  const [pickingFlowerId, setPickingFlowerId] = useState(null);
  const [vendorPicks, setVendorPicks] = useState({}); // { [flowerId]: { vendorCost, vendorOfferId, vendorName, arrivalDate } }
  const [offerResults, setOfferResults] = useState({}); // flowerId -> last search offer count
  const [subsForFlowerId, setSubsForFlowerId] = useState(null); // flowerId whose SubstitutePanel is open

  useEffect(() => {
    let active = true;
    formulaApi
      .getPriceList()
      .then((pl) => {
        if (!active) return;
        setPriceList(pl);
        // Reopening on a saved formula? Rehydrate the full builder state from
        // its snapshot so the exact vase, stems and counts come back — not just
        // the breakdown text. Otherwise start fresh at the default labor rate.
        const snap = initialFormula && initialFormula.formula_snapshot;
        if (snap && typeof snap === 'object') {
          if (snap.selectedVaseId !== undefined) setSelectedVaseId(snap.selectedVaseId);
          if (snap.flowerCounts) setFlowerCounts(snap.flowerCounts);
          if (snap.quantity != null) setQuantity(snap.quantity);
          setLaborRate(snap.laborRate != null ? snap.laborRate : (pl.labor?.defaultRate ?? 22.5) / 100);
          if (snap.pricingMode) setPricingMode(snap.pricingMode);
          if (snap.budget != null) setBudget(snap.budget);
          if (snap.budgetMode) setBudgetMode(snap.budgetMode);
          if (snap.nameOverride !== undefined) setNameOverride(snap.nameOverride);
          if (Array.isArray(snap.customFlowers)) setCustomFlowers(snap.customFlowers);
          if (snap.selectedVaseId) setActiveTab('flowers');
        } else {
          setLaborRate((pl.labor?.defaultRate ?? 22.5) / 100);
        }
        // Seed vendorPicks: prefer the full snapshot (preserves pulledPrice/adjPct),
        // fall back to stem rows (shapes entries with adjPct=0 so UI still renders).
        if (snap?.vendorPicks && typeof snap.vendorPicks === 'object' && Object.keys(snap.vendorPicks).length) {
          setVendorPicks(snap.vendorPicks);
        } else {
          const seeded = {};
          (initialFormula?.stems || []).forEach((st) => {
            if (st.vendor_offer_id) {
              const cost = Number(st.wholesale_unit_cost) || 0;
              seeded[st.stem_key] = {
                pulledPrice: cost,
                adjPct: 0,
                vendorCost: cost,
                vendorOfferId: st.vendor_offer_id,
                vendorName: null,
                arrivalDate: null,
                unitCount: Number(st.unit_count) || null,
              };
            }
          });
          if (Object.keys(seeded).length) setVendorPicks(seeded);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (active) {
          setLoadError(getErrorMessage(err));
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
    // Hydrate once on mount from the formula passed in; the builder is
    // remounted whenever it opens on a different line item.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const el = budgetSectionRef.current;
      if (!el) return;
      setShowStickyBanner(el.getBoundingClientRect().bottom < 0);
    };
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', handleScroll, { capture: true });
  }, []);

  const vase = useMemo(
    () => priceList.vases.find((v) => v.id === selectedVaseId) || null,
    [priceList.vases, selectedVaseId]
  );
  // Saved flowers + any session-only custom stems.
  const allFlowers = useMemo(
    () => [...priceList.flowers, ...customFlowers],
    [priceList.flowers, customFlowers]
  );

  const flowerMarkup = priceList.labor?.flowerMarkup ?? 3.5;
  const pickerDate = eventDate
    ? new Date(Date.parse(`${eventDate}T00:00:00Z`) - 3 * 86400000).toISOString().slice(0, 10)
    : null;

  const applyVendorPick = (flowerId, offer) => {
    const pulled = Number(offer.price_per_stem) || 0;
    setVendorPicks((prev) => ({
      ...prev,
      [flowerId]: {
        pulledPrice: pulled,
        adjPct: 0,
        vendorCost: pulled,
        vendorOfferId: offer.vendor_offer_id || offer.id || null,
        vendorName: offer.vendor_name || null,
        arrivalDate: offer.attributes?.arrival_date || null,
        unitCount: Number(offer.unit_count) || null,
      },
    }));
    setPickingFlowerId(null);
  };
  const setVendorAdjustment = (flowerId, pct) => {
    setVendorPicks((prev) => {
      const p = prev[flowerId]; if (!p) return prev;
      const adjPct = Number(pct) || 0;
      const base = Number(p.pulledPrice) || 0;
      return { ...prev, [flowerId]: { ...p, adjPct, vendorCost: Number((base * (1 + adjPct / 100)).toFixed(4)) } };
    });
  };
  const clearVendorPick = (flowerId) => {
    setVendorPicks((prev) => { const n = { ...prev }; delete n[flowerId]; return n; });
  };

  // Swap a flower to a curated substitute: the substitute becomes a custom
  // flower carrying the original's stem count; the original drops off the
  // arrangement (count → 0) and its pick is cleared. If the panel found a live
  // offer, apply it (re-pull); otherwise open the picker to price the new stem.
  const swapToSubstitute = (originalId, substituteName, offer) => {
    const orig = allFlowers.find((f) => f.id === originalId);
    const newId = `sub-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const newFlower = {
      id: newId,
      name: substituteName,
      pricePerStem: 0,
      photoUrl: '',
      category: orig?.category || '',
      defaultAdd: 1,
      isCustom: true,
    };
    setCustomFlowers((c) => [...c, newFlower]);
    const count = flowerCounts[originalId] || 0;
    setFlowerCounts((c) => ({ ...c, [newId]: count, [originalId]: 0 }));
    clearVendorPick(originalId);
    setSubsForFlowerId(null);
    if (offer) {
      applyVendorPick(newId, offer);
    } else {
      setPickingFlowerId(newId);
    }
  };

  // Overlay vendor pricing on top of allFlowers so the rest of the
  // component (subtotal, tile display, formula list) sees vendor-priced stems.
  const pricedFlowers = allFlowers.map((f) => {
    const pick = vendorPicks[f.id];
    if (!pick) return f;
    return {
      ...f,
      pricePerStem: effectiveRetail(pick.vendorCost, flowerMarkup),
      vendorCost: pick.vendorCost,
      pulledPrice: pick.pulledPrice,
      adjPct: pick.adjPct,
      vendorOfferId: pick.vendorOfferId,
      vendorName: pick.vendorName,
      arrivalDate: pick.arrivalDate,
      unitCount: pick.unitCount,
    };
  });

  const subtotal = arrangementSubtotal(vase, flowerCounts, pricedFlowers);

  const labor = laborAmount(subtotal, laborRate);
  const total = arrangementTotal(subtotal, laborRate);
  const stems = totalStemCount(flowerCounts);
  const qty = Math.max(1, Number(quantity) || 1);
  // The budget field holds either a per-arrangement amount or a total; the
  // meter always works in per-arrangement terms.
  const enteredBudget = parseFloat(budget) || 0;
  const budgetNum =
    budgetMode === 'total' ? enteredBudget / qty : enteredBudget;
  // Each ±click moves the per-arrangement budget by $5 — which in total mode
  // means stepping the total by $5 × the number of arrangements.
  const budgetStep = budgetMode === 'total' ? 5 * qty : 5;
  const hasBudget = pricingMode === 'budget' && budgetNum > 0;
  const remaining = budgetNum - total;
  const overBudget = hasBudget && total > budgetNum;
  const meterPct = hasBudget ? Math.min(100, (total / budgetNum) * 100) : 0;
  // Light→dark gradient fill so the bar reads as the mockup did. Green
  // gradient holds through the normal composing range; shifts to an amber
  // gradient only in the last stretch before the cap, red (striped) over.
  const meterColor =
    meterPct >= 90
      ? 'bg-dot-yellow'
      : 'bg-floral-700';
  // How far past budget, as a share of the budget — drives the red overflow
  // segment so the meter shows how over we are, not just that we are.
  const overage = hasBudget ? Math.max(0, total - budgetNum) : 0;
  const overagePct = hasBudget
    ? Math.min(100, (overage / budgetNum) * 100)
    : 0;

  // Whether to show the big remaining-spend countdown instead of the
  // editable budget value in the field. We need stems on the order and a
  // budget entered; staff stays in display mode until they tap the field
  // or a stepper, at which point they see the budget value for editing.
  const showRemaining =
    pricingMode === 'budget' && !editingBudget && hasBudget && total > 0;
  // Mode-aware displays — match the units the budget input is showing.
  const modeMultiplier = budgetMode === 'total' ? qty : 1;
  const composedDisplay = total * modeMultiplier;
  const remainingDisplay = remaining * modeMultiplier;
  const overageDisplay = overage * modeMultiplier;
  const budgetCapDisplay =
    budgetMode === 'total' ? enteredBudget : budgetNum;
  const remainingFieldStr = overBudget
    ? '0'
    : Math.max(0, remainingDisplay).toFixed(0);

  const margin = vendorMargin(pricedFlowers, flowerCounts);

  const selectedFlowers = pricedFlowers.filter((f) => (flowerCounts[f.id] || 0) > 0);
  const derivedName = vase ? `Custom Arrangement — ${vase.name}` : 'Custom Arrangement';
  const itemName = nameOverride !== null ? nameOverride : derivedName;
  const canCommit = subtotal > 0;
  // When the builder opens on a line item that's already on a saved order
  // and already carries a price, the calculator should keep that price
  // instead of overriding with its own total — surface that in the CTA.
  const existingPriceLocked =
    !!existingItem &&
    existingItem.existingLineItem === true &&
    Number(existingItem.price) > 0;
  const existingPriceValue = existingPriceLocked
    ? Number(existingItem.price)
    : 0;
  // Standalone (full-page) use passes no onCommit — let it grow with the
  // viewport instead of scrolling inside a capped height.
  const standalone = !onCommit;
  // Formula-list stepper buttons: larger touch targets on the full-page
  // (iPad) view, more compact in the desktop modal.
  const listStepBtn = standalone ? 'w-8 h-8 text-lg' : 'w-6 h-6 text-sm';

  const vaseCost = (v) =>
    (Number(v?.basePrice) || 0) + (Number(v?.setupPrice) || 0);

  const visibleVases = priceList.vases.filter((v) => fuzzyMatch(search, v.name));
  const visibleFlowers = pricedFlowers.filter((f) =>
    fuzzyMatch(search, f.name)
  );
  // Flower type sub-tabs — All plus whichever categories actually exist.
  const flowerTypeTabs = [
    { id: 'all', label: 'All' },
    ...['focal', 'line', 'mass', 'filler']
      .filter((cat) => pricedFlowers.some((f) => f.category === cat))
      .map((cat) => ({ id: cat, label: CATEGORY_META[cat].label })),
    ...(pricedFlowers.some((f) => !CATEGORY_META[f.category])
      ? [{ id: 'other', label: 'Other' }]
      : []),
  ];
  const filteredFlowers =
    showTypeTabs && flowerTypeFilter !== 'all'
      ? visibleFlowers.filter((f) =>
          flowerTypeFilter === 'other'
            ? !CATEGORY_META[f.category]
            : f.category === flowerTypeFilter
        )
      : visibleFlowers;

  // On the full-page standalone view the budget/quantity fields get a tall
  // square footprint; in the inline modal they stay compact.
  const fieldBoxCls = standalone
    ? `${fieldBox} min-w-[10rem] h-32 justify-center`
    : fieldBox;
  const valueTextCls = standalone ? 'text-4xl' : 'text-2xl';
  const dollarTextCls = standalone ? 'text-3xl' : 'text-xl';
  const qtyInputWidth = standalone ? 'w-16' : 'w-10';

  const selectVase = (id) => {
    const next = selectedVaseId === id ? null : id;
    setSelectedVaseId(next);
    // Picking a vase advances to Flowers; deselecting stays put.
    if (next) {
      setActiveTab('flowers');
      setSearch('');
    }
  };
  // First add (from zero) uses the flower's configured default quantity;
  // every add after that increments by one.
  const addFlower = (id) => {
    const flower = pricedFlowers.find((f) => f.id === id);
    setFlowerCounts((c) => {
      const current = c[id] || 0;
      const step =
        current === 0
          ? Math.max(1, Math.round(Number(flower?.defaultAdd) || 1))
          : 1;
      return { ...c, [id]: current + step };
    });
  };
  const adjustFlower = (id, delta) =>
    setFlowerCounts((c) => {
      const next = Math.max(0, (c[id] || 0) + delta);
      const updated = { ...c };
      if (next === 0) delete updated[id];
      else updated[id] = next;
      return updated;
    });

  const submitCustomStem = () => {
    const name = customDraft.name.trim();
    const price = Number(customDraft.price);
    if (!name || !(price > 0)) return;
    const stem = {
      id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name,
      pricePerStem: price,
      photoUrl: '',
      category: '',
      defaultAdd: 1,
      isCustom: true,
    };
    setCustomFlowers((c) => [...c, stem]);
    setFlowerCounts((c) => ({ ...c, [stem.id]: 1 }));
    setCustomDraft({ name: '', price: '' });
    setShowCustomForm(false);
    if (showTypeTabs) setFlowerTypeFilter('all');
  };

  const renderFlowerTile = (f) => (
    <div key={f.id} className="flex flex-col gap-0.5">
      <Tile
        item={f}
        selected={(flowerCounts[f.id] || 0) > 0}
        count={flowerCounts[f.id] || 0}
        onClick={(e) =>
          e.metaKey || e.ctrlKey ? adjustFlower(f.id, -1) : addFlower(f.id)
        }
      />
      {pickingFlowerId === f.id && (
        <VendorOfferPicker
          stem={f.name}
          date={pickerDate}
          onPick={(offer) => applyVendorPick(f.id, offer)}
          onResult={(count) => setOfferResults((r) => ({ ...r, [f.id]: count }))}
        />
      )}
      {(flowerCounts[f.id] || 0) > 0 && (() => {
        const pick = vendorPicks[f.id];
        const emphasis = substituteEmphasis({
          hasPick: !!pick,
          noOffersForDate: offerResults[f.id] === 0,
          neededStems: (flowerCounts[f.id] || 0) * qty,
          boxSize: Number(pick?.unitCount) || 0,
        });
        const open = subsForFlowerId === f.id;
        return (
          <div className="px-0.5">
            <button
              type="button"
              onClick={() => setSubsForFlowerId(open ? null : f.id)}
              className={`text-[11px] hover:underline ${
                emphasis ? 'text-warn-fg font-semibold' : 'text-gray-500'
              }`}
            >
              {emphasis === 'unavailable'
                ? '⚠ No offer — find a substitute'
                : emphasis === 'over-min'
                ? '⚠ Box waste — find a substitute'
                : 'Substitutes'}
            </button>
            {open && (
              <SubstitutePanel
                stemName={f.name}
                date={pickerDate}
                reason={emphasis}
                onSwap={(name, offer) => swapToSubstitute(f.id, name, offer)}
                onClose={() => setSubsForFlowerId(null)}
              />
            )}
          </div>
        );
      })()}
    </div>
  );

  const stepBudget = (delta) => {
    const next = Math.max(0, (parseFloat(budget) || 0) + delta);
    setBudget(next === 0 ? '' : String(next));
  };
  // Tapping a budget stepper flips the field out of the remaining-countdown
  // display and focuses the input so the new budget value is visible and
  // editable. Clicking elsewhere blurs the input and returns to countdown.
  const handleStepBudget = (delta) => {
    stepBudget(delta);
    setEditingBudget(true);
    setTimeout(() => budgetInputRef.current?.focus(), 0);
  };
  const stepQty = (delta) => {
    setQuantity(String(Math.max(1, (parseInt(quantity, 10) || 1) + delta)));
  };
  // Switching per-arrangement ↔ total converts the entered value so the
  // actual budget is preserved.
  const changeBudgetMode = (mode) => {
    if (mode === budgetMode) return;
    const cur = parseFloat(budget) || 0;
    if (cur > 0) {
      const converted = mode === 'total' ? cur * qty : cur / qty;
      setBudget(String(Math.round(converted * 100) / 100));
    }
    setBudgetMode(mode);
  };

  const reset = () => {
    setSelectedVaseId(null);
    setFlowerCounts({});
    setQuantity(1);
    setBudget('');
    setBudgetMode('perArrangement');
    setEditingBudget(false);
    setLaborRate((priceList.labor?.defaultRate ?? 22.5) / 100);
    setNameOverride(null);
    setActiveTab('vase');
  };

  // Commit the formula to the order at a specific per-arrangement price.
  // Defaults to the exact formula total when no price is passed.
  const handleCommit = (priceValue = total) => {
    if (!onCommit || !canCommit) return;
    const committedPrice = Number(priceValue) || 0;
    onCommit({
      name: itemName,
      price: committedPrice.toFixed(2),
      quantity: qty,
      instructions: buildBreakdownText(pricedFlowers, flowerCounts, qty),
      // Structured composition so the formula can be reloaded and later priced
      // against wholesale vendor offers. Snake_case mirrors the DB columns so it
      // round-trips through the proposal payload with no field mapping. Existing
      // (order) callers simply ignore this extra `formula` key.
      formula: {
        vase_ref: selectedVaseId || null,
        vase_cost: vase ? vaseCost(vase) : 0,
        labor_rate: laborRate,
        labor_amount: labor,
        arrangement_qty: qty,
        retail_price: committedPrice,
        formula_snapshot: {
          selectedVaseId,
          flowerCounts,
          quantity: qty,
          laborRate,
          pricingMode,
          budget,
          budgetMode,
          nameOverride,
          customFlowers,
          vendorPicks,
        },
        stems: selectedFlowers.map((f, i) => toStemRow(f, i, flowerCounts)),
      },
    });
  };

  // Quick-pick drop-in prices for the inline (new-order) modal. With a
  // budget set: [exact formula total, budget (recommended), budget + $5].
  // Without a budget: [exact, rounded up to next $5, that + $5]. The
  // dropped price is still editable on the order line afterward.
  const ceilTo5 = (x) => Math.ceil(x / 5) * 5;
  const priceOptions = (() => {
    if (hasBudget) {
      return [
        { key: 'formula', label: 'Formula', value: total },
        { key: 'budget', label: 'Budget', value: budgetNum, recommended: true },
        { key: 'plus', label: 'Budget +$5', value: budgetNum + 5 },
      ];
    }
    const mid = ceilTo5(total) > total ? ceilTo5(total) : total + 5;
    return [
      { key: 'formula', label: 'Formula', value: total },
      { key: 'round', label: 'Rounded', value: mid, recommended: true },
      { key: 'plus', label: '+$5', value: mid + 5 },
    ];
  })();

  // Picker tabs. On the Formulas page they sit in the PageHeader band; in the
  // order / proposal modal they head the picker pane.
  const tabButtons = TABS.map((tab) => (
    <button
      key={tab.id}
      type="button"
      onClick={() => {
        setActiveTab(tab.id);
        setSearch('');
      }}
      className={`tab ${activeTab === tab.id ? 'tab-active' : ''}`}
      aria-pressed={activeTab === tab.id}
    >
      {tab.label}
      {tab.id === 'flowers' && (
        <span
          title={
            showTypeTabs
              ? 'Combine flowers'
              : 'Split flowers by type'
          }
          onClick={(e) => {
            e.stopPropagation();
            setActiveTab('flowers');
            setSearch('');
            setShowTypeTabs((s) => !s);
            setFlowerTypeFilter('all');
          }}
          className={`inline-flex rounded p-0.5 transition-colors duration-[120ms] ${
            showTypeTabs
              ? 'bg-floral-100 text-floral-900'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
          }`}
        >
          <RowsIcon size={14} strokeWidth={2} />
        </span>
      )}
    </button>
  ));

  const segItem = (active) => `seg-item flex-1 ${active ? 'seg-item-active' : ''}`;
  const row = 'flex justify-between gap-3 text-gray-600';

  return (
    <div className="flex flex-col w-full">
      {/* Header: the page band on Formulas, a dialog title in the modal */}
      {standalone ? (
        <PageHeader
          title="Formula calculator"
          meta="Build an arrangement from the price list: vase, flowers and labor."
          tabs={tabButtons}
        />
      ) : (
        <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <FlowerIcon size={18} strokeWidth={2} className="text-floral-700" />
            <h3 className="text-[17px] font-semibold tracking-[-0.01em] text-gray-900">Formula calculator</h3>
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="btn-ghost btn-sm px-1.5"
              title="Close"
              aria-label="Close"
            >
              <XIcon size={18} strokeWidth={2} />
            </button>
          )}
        </div>
      )}

      {/* Mobile sticky banner — appears when budget section scrolls out of view */}
      {showStickyBanner && (
        <div className="lg:hidden sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-3">
          <span className={`rounded-full px-3 py-1 text-sm font-semibold tabular-nums whitespace-nowrap flex-shrink-0 ${
            overBudget ? 'bg-danger-bg text-danger-fg' : 'bg-floral-100 text-floral-900'
          }`}>
            {formatCurrency(total)}
          </span>
          <div className="flex-1 min-w-0 flex flex-col gap-1">
            <span className={`text-xs tabular-nums ${overBudget ? 'text-danger-fg' : 'text-gray-500'}`}>
              {qty}×{hasBudget ? (overBudget ? ` · ${formatCurrency(Math.abs(remaining))} over` : ` · ${formatCurrency(remaining)} left`) : ''}
            </span>
            {hasBudget && (
              <div className="h-0.5 bg-floral-100 rounded-full w-full">
                <div
                  className={`h-0.5 rounded-full transition-all ${overBudget ? 'bg-danger w-full' : 'bg-floral-700'}`}
                  style={overBudget ? undefined : { width: `${meterPct}%` }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="p-10 text-center text-sm text-gray-600">Loading price list…</div>
      )}
      {loadError && !loading && (
        <div className="p-10 text-center text-sm text-danger-fg">{loadError}</div>
      )}

      {!loading && !loadError && (
        <div className={standalone
          ? 'grid items-start gap-4 px-4 sm:px-7 pt-5 pb-10 lg:grid-cols-[minmax(0,1fr)_420px]'
          : 'flex flex-col lg:flex-row'}
        >
          {/* Left pane — tabbed picker */}
          <div className={standalone
            ? `order-last lg:order-first min-w-0 ${RAIL_CARD}`
            : 'order-last lg:order-first lg:w-3/5 border-b lg:border-b-0 lg:border-r border-gray-200'}
          >
            {!standalone && (
              <div className="tab-list px-3">{tabButtons}</div>
            )}

            {activeTab === 'flowers' && showTypeTabs && (
              <div className="flex flex-wrap gap-1.5 px-4 pt-3">
                {flowerTypeTabs.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setFlowerTypeFilter(t.id)}
                    aria-pressed={flowerTypeFilter === t.id}
                    className={`chip ${flowerTypeFilter === t.id ? 'chip-selected' : ''}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            )}

            {activeTab !== 'labor' && (
              <div className="px-4 pt-3">
                <div className="relative">
                  <SearchIcon
                    size={16}
                    strokeWidth={2}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={`Search ${activeTab === 'vase' ? 'vases' : 'flowers'}…`}
                    className="input-field pl-9"
                  />
                </div>
              </div>
            )}

            <div className={`p-4 ${standalone ? '' : 'max-h-[58vh] overflow-y-auto'}`}>
              {activeTab === 'vase' && (
                priceList.vases.length === 0 ? (
                  <p className="text-sm text-gray-600 py-8 text-center">
                    No vases configured yet. Add them in Settings → Formulas.
                  </p>
                ) : visibleVases.length === 0 ? (
                  <p className="text-sm text-gray-600 py-8 text-center">
                    No vases match “{search}”.
                  </p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {visibleVases.map((v) => (
                      <Tile
                        key={v.id}
                        item={v}
                        selected={selectedVaseId === v.id}
                        count={0}
                        price={vaseCost(v)}
                        delta={
                          vase && vase.id !== v.id
                            ? vaseCost(v) - vaseCost(vase)
                            : null
                        }
                        onClick={() => selectVase(v.id)}
                      />
                    ))}
                  </div>
                )
              )}

              {activeTab === 'flowers' && (
                <>
                  <p className="text-xs text-gray-500 mb-2">
                    Tap to add a stem · ⌘/Ctrl + Click to remove one.
                  </p>
                  {pricedFlowers.length > 0 && filteredFlowers.length === 0 && (
                    <p className="text-sm text-gray-600 mb-2 text-center">
                      No flowers match.
                    </p>
                  )}
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {filteredFlowers.map(renderFlowerTile)}
                    <button
                      type="button"
                      onClick={() => setShowCustomForm(true)}
                      className="group relative flex flex-col rounded-md border border-dashed border-gray-300 text-left transition-colors duration-[120ms] overflow-hidden hover:border-floral-700 bg-white"
                      title="Add a one-off custom stem"
                    >
                      <div className="aspect-square w-full bg-gray-50 flex items-center justify-center text-gray-500 group-hover:text-floral-700">
                        <PlusIcon size={28} strokeWidth={2} />
                      </div>
                      <div className="px-2 py-1.5 text-xs font-medium text-gray-600 truncate group-hover:text-floral-700">
                        Custom stem
                      </div>
                    </button>
                  </div>
                </>
              )}

              {activeTab === 'labor' && (
                <div className="py-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Labor is added as a percentage of the arrangement subtotal
                    (vase, setup and flowers). It always applies.
                  </p>
                  <div className="flex justify-center">
                    <div className="seg">
                      {(priceList.labor?.rates || []).map((rate) => {
                        const frac = rate / 100;
                        const on = Math.abs(laborRate - frac) < 0.0001;
                        return (
                          <button
                            key={rate}
                            type="button"
                            onClick={() => setLaborRate(frac)}
                            aria-pressed={on}
                            className={`seg-item px-5 tabular-nums ${on ? 'seg-item-active' : ''}`}
                          >
                            {rate}%
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right pane — budget, formula and the estimate. Sticks to the top
              of the scroll container on large screens so the budget, formula
              and totals stay in view while the tile grid scrolls. self-start
              keeps it content-height (flex would otherwise stretch it and
              defeat sticky); the pane scrolls internally if it ever exceeds
              the viewport. In the modal the cap must track the host modal's
              max-h-[92vh], not the window: capped to 100vh the pane can be
              taller than the modal's scrollport, and because it's sticky the
              overhang (the add-to-order buttons) can never be scrolled into
              view on short windows. On the Formulas page it is the right
              rail (cards, no inner padding). */}
          <div ref={budgetSectionRef} className={standalone
            ? 'order-first lg:order-last flex flex-col gap-4 lg:sticky lg:top-5 lg:self-start lg:max-h-[calc(100vh-2.5rem)] lg:overflow-y-auto'
            : 'order-first lg:order-last lg:w-2/5 p-4 flex flex-col gap-4 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(92vh-2rem)] lg:overflow-y-auto'}
          >
            {/* Budget / estimate inputs */}
            <section className={RAIL_CARD}>
              <div className={RAIL_HEAD}>
                <h2 className="text-[15px] font-semibold text-gray-900">Pricing</h2>
              </div>
              <div className="flex flex-col gap-4 px-[18px] py-4">
                {/* Mode toggle */}
                <div className="seg flex w-full">
                  {[
                    { id: 'budget', label: 'Budget' },
                    { id: 'estimate', label: 'Estimate' },
                  ].map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setPricingMode(m.id)}
                      aria-pressed={pricingMode === m.id}
                      className={segItem(pricingMode === m.id)}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>

                {/* Budget + Quantity */}
                <div>
                  <div className="flex flex-wrap gap-x-4 gap-y-3 justify-center">
                    {pricingMode === 'budget' && (
                      <div>
                        <div className="h-6 flex items-center justify-center mb-1">
                          <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5 text-[11px] leading-none">
                            {[
                              { id: 'perArrangement', label: 'Per arrangement' },
                              { id: 'total', label: 'Total' },
                            ].map((m) => (
                              <button
                                key={m.id}
                                type="button"
                                onClick={() => changeBudgetMode(m.id)}
                                aria-pressed={budgetMode === m.id}
                                className={`rounded px-2 py-1 font-semibold transition-colors duration-[120ms] ${
                                  budgetMode === m.id
                                    ? 'bg-floral-700 text-white'
                                    : 'text-gray-600 hover:text-gray-900'
                                }`}
                              >
                                {m.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className={fieldBoxCls}>
                          <button
                            type="button"
                            onClick={() => handleStepBudget(-budgetStep)}
                            className={`${circleBtn} px-2.5 h-7 text-xs`}
                          >
                            −${budgetStep}
                          </button>
                          {showRemaining ? (
                            <button
                              type="button"
                              onClick={() => {
                                setEditingBudget(true);
                                setTimeout(
                                  () => budgetInputRef.current?.focus(),
                                  0
                                );
                              }}
                              className="flex items-center justify-center gap-0.5 flex-1 cursor-text"
                            >
                              <span
                                className={`${dollarTextCls} font-semibold ${
                                  overBudget ? 'text-danger-fg' : 'text-gray-900'
                                }`}
                              >
                                $
                              </span>
                              <span
                                className={`${valueTextCls} font-semibold tabular-nums ${
                                  overBudget ? 'text-danger-fg' : 'text-gray-900'
                                }`}
                              >
                                {remainingFieldStr}
                              </span>
                            </button>
                          ) : (
                            <div className="flex items-center justify-center gap-0.5 flex-1">
                              <span
                                className={`${dollarTextCls} font-semibold ${
                                  budgetNum > 0 ? 'text-gray-900' : 'text-gray-400'
                                }`}
                              >
                                $
                              </span>
                              <input
                                ref={budgetInputRef}
                                type="text"
                                inputMode="decimal"
                                value={budget}
                                onChange={(e) =>
                                  setBudget(e.target.value.replace(/[^0-9.]/g, ''))
                                }
                                onFocus={() => setEditingBudget(true)}
                                onBlur={() => setEditingBudget(false)}
                                placeholder="0"
                                style={{ width: `${(budget.length || 1)}ch` }}
                                className={`${valueTextCls} font-semibold text-gray-900 text-center tabular-nums bg-transparent border-0 p-0 focus:ring-0 focus:outline-none`}
                              />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => handleStepBudget(budgetStep)}
                            className={`${circleBtn} px-2.5 h-7 text-xs`}
                          >
                            +${budgetStep}
                          </button>
                        </div>
                        {showRemaining ? (
                          <p className="mt-1 text-xs text-gray-500 text-center tabular-nums">
                            of {formatCurrency(budgetCapDisplay)} left
                          </p>
                        ) : (
                          <>
                            {budgetMode === 'total' && enteredBudget > 0 && (
                              <p className="mt-1 text-xs text-gray-500 text-center tabular-nums">
                                {formatCurrency(budgetNum)} per arrangement
                              </p>
                            )}
                            {budgetMode === 'perArrangement' &&
                              qty > 1 &&
                              enteredBudget > 0 && (
                                <p className="mt-1 text-xs text-gray-500 text-center tabular-nums">
                                  {formatCurrency(enteredBudget * qty)} total
                                </p>
                              )}
                          </>
                        )}
                      </div>
                    )}

                    <div>
                      <div className="h-6 flex items-center justify-center mb-1">
                        <span className="text-xs font-medium text-gray-600">
                          Arrangements
                        </span>
                      </div>
                      <div className={fieldBoxCls}>
                        <button
                          type="button"
                          onClick={() => stepQty(-1)}
                          className={`${circleBtn} w-8 h-8 text-lg`}
                          aria-label="Remove one arrangement"
                        >
                          −
                        </button>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={quantity}
                          onChange={(e) =>
                            setQuantity(e.target.value.replace(/[^0-9]/g, ''))
                          }
                          className={`${qtyInputWidth} ${valueTextCls} font-semibold tabular-nums text-gray-900 text-center bg-transparent border-0 p-0 focus:ring-0 focus:outline-none`}
                        />
                        <button
                          type="button"
                          onClick={() => stepQty(1)}
                          className={`${circleBtn} w-8 h-8`}
                          aria-label="Add one arrangement"
                        >
                          <PlusIcon size={16} strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {hasBudget && (
                    <div className="mt-3">
                      <div className="relative h-1.5 rounded-full bg-floral-100 overflow-hidden">
                        <div
                          className={`absolute inset-y-0 left-0 transition-all ${
                            overBudget ? 'bg-danger' : meterColor
                          }`}
                          style={{ width: `${meterPct}%` }}
                        />
                        {overBudget && (
                          <div
                            className="absolute inset-y-0 right-0 bg-danger-fg transition-all"
                            style={{ width: `${overagePct}%` }}
                          />
                        )}
                      </div>
                      <p
                        className={`mt-1.5 text-[13px] tabular-nums ${
                          overBudget ? 'text-danger-fg font-medium' : 'text-gray-600'
                        }`}
                      >
                        <span className="text-gray-900 font-semibold">
                          {formatCurrency(composedDisplay)}
                        </span>{' '}
                        composed ·{' '}
                        {overBudget ? (
                          <>
                            <span className="font-semibold">
                              {formatCurrency(overageDisplay)}
                            </span>{' '}
                            over budget
                          </>
                        ) : (
                          <>
                            <span className="text-gray-900 font-semibold">
                              {formatCurrency(remainingDisplay)}
                            </span>{' '}
                            still to spend
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Formula list */}
            <section className={RAIL_CARD}>
              <div className={RAIL_HEAD}>
                <h2 className="text-[15px] font-semibold text-gray-900">Formula</h2>
                <span className="text-xs tabular-nums text-gray-500">
                  {stems} stem{stems === 1 ? '' : 's'}
                </span>
              </div>
              <div
                className={`divide-y divide-gray-200 ${
                  standalone ? '' : 'max-h-48 overflow-y-auto'
                }`}
              >
                {!vase && selectedFlowers.length === 0 && (
                  <p className="text-[13px] text-gray-600 px-[18px] py-4 text-center">
                    Nothing selected yet — start with a vase.
                  </p>
                )}
                {vase && (
                  <div className="flex items-center gap-2 px-[18px] py-2 text-[13px]">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <span className="text-gray-900 truncate">
                        {vase.name}
                      </span>
                      <span className="shrink-0 text-[10px] font-semibold px-1.5 py-px rounded-full bg-neutral-bg text-neutral-fg">
                        Vase
                      </span>
                    </div>
                    <span className="w-20 text-right text-gray-600 tabular-nums">
                      {formatCompact(
                        (Number(vase.basePrice) || 0) + (Number(vase.setupPrice) || 0)
                      )}
                    </span>
                  </div>
                )}
                {selectedFlowers.map((f) => {
                  const per = flowerCounts[f.id];
                  const linePrice = (Number(f.pricePerStem) || 0) * per;
                  return (
                    <div
                      key={f.id}
                      className="flex items-center gap-2 px-[18px] py-2 text-[13px]"
                    >
                      <div className="flex items-center gap-1.5 flex-1 min-w-0">
                        <span className="text-gray-900 truncate">{f.name}</span>
                        {CATEGORY_META[f.category] && (
                          <span
                            className={`shrink-0 text-[10px] font-semibold px-1.5 py-px rounded-full ${
                              CATEGORY_META[f.category].cls
                            }`}
                          >
                            {CATEGORY_META[f.category].label}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => adjustFlower(f.id, -1)}
                          className={`${listStepBtn} flex items-center justify-center rounded-md border border-gray-300 bg-gray-50 text-gray-900 hover:bg-gray-200 leading-none`}
                        >
                          −
                        </button>
                        <span className="w-12 text-center tabular-nums text-gray-900">
                          {qty > 1 ? `${per}/${per * qty}` : per}
                        </span>
                        <button
                          type="button"
                          onClick={() => adjustFlower(f.id, 1)}
                          className={`${listStepBtn} flex items-center justify-center rounded-md border border-gray-300 bg-gray-50 text-gray-900 hover:bg-gray-200 leading-none`}
                        >
                          +
                        </button>
                      </div>
                      <div className="w-20 text-right text-gray-600 tabular-nums">
                        {formatCompact(linePrice)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Estimate — the totals pattern (as Order detail's Total card) */}
            <section className={RAIL_CARD}>
              <div className={RAIL_HEAD}>
                <h2 className="text-[15px] font-semibold text-gray-900">Estimate</h2>
              </div>
              <div className="flex flex-col gap-2 px-[18px] py-4 text-[13px]">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-gray-600">Per arrangement</span>
                  <span className="tabular-nums text-[32px] font-semibold leading-none tracking-tight text-gray-900">
                    {formatCurrency(total)}
                  </span>
                </div>
                <div className="mt-1 flex flex-col gap-2">
                  <div className={row}>
                    <span>Subtotal</span>
                    <span className="tabular-nums text-gray-900">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className={row}>
                    <span>Labor ({(laborRate * 100).toFixed(1)}%)</span>
                    <span className="tabular-nums text-gray-900">{formatCurrency(labor)}</span>
                  </div>
                </div>
                {margin && (
                  <div className="text-xs tabular-nums text-gray-500">
                    Wholesale {formatCurrency(margin.wholesale)} · Retail {formatCurrency(margin.retail)} · Margin {margin.marginPct.toFixed(0)}% (vendor-priced)
                  </div>
                )}
                {qty > 1 && (
                  <div className="flex items-baseline justify-between gap-3 border-t border-gray-200 pt-2 font-semibold text-gray-900">
                    <span>Order total (×{qty})</span>
                    <span className="tabular-nums text-[15px]">{formatCurrency(total * qty)}</span>
                  </div>
                )}
              </div>

              {!onCommit && (
                <div className="border-t border-gray-200 px-[18px] py-3">
                  <button
                    type="button"
                    onClick={reset}
                    className="btn-secondary w-full"
                  >
                    Start over
                  </button>
                </div>
              )}
            </section>

            {/* Actions — sticky at the bottom of whichever container scrolls
                (the pane on desktop, the modal body on mobile) so the
                add-to-order buttons stay visible while the content above
                scrolls. Negative margins bleed the white footer to the pane's
                edges so scrolled content doesn't peek through the padding. */}
            {onCommit && (
                <div className="sticky bottom-0 -mx-4 -mb-4 px-4 pb-4 pt-3 bg-white border-t border-gray-200">
                  <label className="input-label" htmlFor="formula-item-name">
                    Item name
                  </label>
                  <input
                    id="formula-item-name"
                    type="text"
                    value={itemName}
                    onChange={(e) => setNameOverride(e.target.value)}
                    className="input-field mb-3"
                  />
                  {existingPriceLocked ? (
                    <>
                      <p className="mb-2 rounded-md bg-floral-100 px-2.5 py-1.5 text-xs text-floral-900">
                        This item already has a price — committing keeps{' '}
                        {formatCurrency(existingPriceValue)} and only adds the
                        formula breakdown.
                      </p>
                      <button
                        type="button"
                        onClick={() => handleCommit(existingPriceValue)}
                        disabled={!canCommit}
                        className="btn-primary w-full"
                      >
                        Add formula · keep {formatCurrency(existingPriceValue)} price
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-gray-500 mb-1.5">
                        Drop into order at — you can still edit the price on the order.
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {priceOptions.map((opt) => (
                          <button
                            key={opt.key}
                            type="button"
                            onClick={() => handleCommit(opt.value)}
                            disabled={!canCommit}
                            className={`flex flex-col items-center py-2 rounded-md transition-colors duration-[120ms] disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 ${
                              opt.recommended
                                ? 'border-2 border-floral-700 bg-floral-50 text-gray-900 hover:bg-floral-100'
                                : 'border border-gray-300 bg-white text-gray-900 hover:border-floral-500 hover:bg-floral-50'
                            }`}
                          >
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                              {opt.label}
                            </span>
                            <span className="text-lg font-semibold tabular-nums">
                              {formatCompact(opt.value)}
                            </span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
            )}
          </div>
        </div>
      )}

      {showCustomForm && (
        <div
          className="fixed inset-0 z-[60] bg-[color:var(--c-backdrop)] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowCustomForm(false);
              setCustomDraft({ name: '', price: '' });
            }
          }}
        >
          <div className="bg-white border border-gray-200 rounded-2xl shadow-lg w-full max-w-sm">
            <div className="px-6 pt-6">
              <h3 className="text-[17px] leading-6 font-semibold tracking-[-0.01em] text-gray-900">
                Add a custom stem
              </h3>
              <p className="mt-1.5 text-[13px] text-gray-600">
                One-off — not saved to Settings.
              </p>
            </div>
            <div className="px-6 py-4 space-y-3">
              <label className="block">
                <span className="input-label">Name</span>
                <input
                  type="text"
                  value={customDraft.name}
                  onChange={(e) =>
                    setCustomDraft((d) => ({ ...d, name: e.target.value }))
                  }
                  placeholder="Seasonal stem"
                  autoFocus
                  className="input-field"
                />
              </label>
              <label className="block">
                <span className="input-label">Price per stem</span>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                    $
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={customDraft.price}
                    onChange={(e) =>
                      setCustomDraft((d) => ({
                        ...d,
                        price: e.target.value.replace(/[^0-9.]/g, ''),
                      }))
                    }
                    className="input-field pl-6 tabular-nums"
                  />
                </div>
              </label>
            </div>
            <div className="px-6 pb-6 pt-1 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCustomForm(false);
                  setCustomDraft({ name: '', price: '' });
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCustomStem}
                disabled={
                  !customDraft.name.trim() || !(Number(customDraft.price) > 0)
                }
                className="btn-primary"
              >
                Add stem
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormulaBuilder;
